/* eslint-disable no-inner-declarations */
/* eslint-disable no-use-before-define */
/* eslint-disable @typescript-eslint/no-use-before-define */
import { ChartView } from 'echarts';
import { graphic } from 'echarts/core';
import Path from 'zrender/lib/graphic/Path.js';
import BoundingRect from 'zrender/lib/core/BoundingRect.js';

/** 旗子标记对应的信息 */
export interface CurvedPointerLayoutInfo {
  /** 文本方框对应的zrender元素 */
  rect: graphic.Rect;
  /** 文本对应的zrender元素 */
  label: graphic.Text;
  /** 曲线对应的zrender元素 */
  line: Path;
  /** 线的圆点对应的zrender元素 */
  circle: graphic.Circle;
  /** 整体容器对应的zrender元素 */
  group: graphic.Group;
  /** 文本和方框所在容器对应的zrender元素 */
  insideGroup: graphic.Group;
  /** 整个标记对应的包围矩形的信息 */
  boundaryRect: BoundingRect;
  /** 旗子标记的显示优先级 */
  weight: number;
}

export interface CurvedPointerLayoutListPrepareInput {
  curvedPointer_group_inside?: graphic.Group;
  curvedPointer_rect?: graphic.Rect;
  curvedPointer_text?: graphic.Text;
  curvedPointer_line?: graphic.Line;
  curvedPointer_circle?: graphic.Circle;
  group?: graphic.Group;
  priority?: number;
}

export type CurvedPointerPartName = keyof Omit<
  CurvedPointerLayoutListPrepareInput,
  'group' | 'priority'
>;

export function getCurvedPointerLayoutListPrepareInput(
  chartView: ChartView
): CurvedPointerLayoutListPrepareInput[] {
  const { group } = chartView;
  const curvedPointerPartMap: {
    [key in CurvedPointerPartName]: string;
  } = {
    curvedPointer_circle: 'circle',
    curvedPointer_group_inside: 'insideGroup',
    curvedPointer_rect: 'rect',
    curvedPointer_line: 'line',
    curvedPointer_text: 'label'
  };
  const curvedPointerLayoutListPrepareInput: CurvedPointerLayoutListPrepareInput[] = [];

  group.eachChild(marker => {
    const input: CurvedPointerLayoutListPrepareInput = {};
    if (marker.ignore) {
      return;
    }
    marker.traverse(el => {
      if (el.ignore) {
        return;
      }

      Object.keys(curvedPointerPartMap).forEach(key => {
        if ((el?.extra?.dvid as CurvedPointerPartName).includes(key)) {
          input[key] = el;
        }
      });
    });

    input.group = marker;
    input.priority = 1;

    curvedPointerLayoutListPrepareInput.push(input);
  });

  return curvedPointerLayoutListPrepareInput;
}

export function prepareCurvedPointerInfoList(
  input: CurvedPointerLayoutListPrepareInput[]
): CurvedPointerLayoutInfo[] {
  const list: CurvedPointerLayoutInfo[] = [];

  input.forEach(rawItem => {
    const transform = rawItem.curvedPointer_rect.getComputedTransform();
    const boundaryRect = rawItem.curvedPointer_rect.getBoundingRect();
    boundaryRect.applyTransform(transform);
    list.push({
      rect: rawItem.curvedPointer_rect,
      label: rawItem.curvedPointer_text,
      line: rawItem.curvedPointer_line,
      circle: rawItem.curvedPointer_circle,
      group: rawItem.group,
      insideGroup: rawItem.curvedPointer_group_inside,
      boundaryRect,
      weight: rawItem.priority
    });
  });

  return list;
}

export function shiftLayoutOnY(
  list: CurvedPointerLayoutInfo[],
  minBound: number,
  maxBound: number
): void {
  const xyDim = 'y';
  const sizeDim = 'height';
  const len = list.length;

  if (len <= 0) {
    return;
  }

  // 根据标记的整体包围盒矩形的y坐标对list进行排序
  list.sort((a, b) => {
    return a.boundaryRect[xyDim] - b.boundaryRect[xyDim];
  });

  let lastPos = 0;
  let delta;
  const SPACE = 6;

  // 遍历list
  for (let i = 0; i < len; i += 1) {
    const item = list[i];
    const rect = item.boundaryRect;
    // delta表示上一个标记到当前标记的距离
    delta = rect[xyDim] - lastPos - SPACE;
    // 如果这个距离为负，表示当前的标记在上一标记的负方向，所以需要按顺序调整位置
    if (delta < 0) {
      shiftList(-delta, i, i + 1);
    }
    lastPos = rect[xyDim] + rect[sizeDim];
  }

  const first = list[0];
  const last = list[len - 1];
  let minGap: number;
  let maxGap: number;
  const labelsDimensionPercentage = 0.8;
  updateMinMaxGap();

  // If ends exceed two bounds, squeeze at most 80%, then take the gap of two bounds.
  if (minGap < 0) {
    squeezeGaps(-minGap, labelsDimensionPercentage);
  }

  if (maxGap < 0) {
    squeezeGaps(maxGap, labelsDimensionPercentage);
  }

  updateMinMaxGap();
  takeBoundsGap(minGap, maxGap, 1);
  takeBoundsGap(maxGap, minGap, -1);

  // Handle bailout when there is not enough space.
  updateMinMaxGap();

  if (minGap < 0) {
    squeezeWhenBailout(-minGap);
  }
  if (maxGap < 0) {
    squeezeWhenBailout(maxGap);
  }

  function updateMinMaxGap() {
    minGap = first.boundaryRect[xyDim] - minBound;
    maxGap = maxBound - last.boundaryRect[xyDim] - last.boundaryRect[sizeDim];
  }

  function shiftList(dlt: number, start: number, end: number) {
    for (let i = start; i < end; i++) {
      const item = list[i];
      const { line, insideGroup } = item;
      const offsetX = line.shape.ex > line.shape.sx ? -dlt : dlt;
      insideGroup.attr({
        y: insideGroup.y + dlt,
        x: insideGroup.x + offsetX
      });
      line.attr({
        shape: {
          ey: line.shape.ey + dlt,
          ex: line.shape.ex + offsetX
        }
      });
    }
  }

  function takeBoundsGap(gapThisBound: number, gapOtherBound: number, moveDir: 1 | -1) {
    if (gapThisBound < 0) {
      // Move from other gap if can.
      const moveFromMaxGap = Math.min(gapOtherBound, -gapThisBound);
      if (moveFromMaxGap > 0) {
        shiftList(moveFromMaxGap * moveDir, 0, len);
        const remained = moveFromMaxGap + gapThisBound;
        if (remained < 0) {
          squeezeGaps(-remained * moveDir, 1);
        }
      } else {
        squeezeGaps(-gapThisBound * moveDir, 1);
      }
    }
  }

  // Squeeze gaps if the labels exceed margin.
  function squeezeGaps(dlt: number, maxSqeezePercent: number) {
    const gaps: number[] = [];
    let totalGaps = 0;
    for (let i = 1; i < len; i += 1) {
      const prevItemRect = list[i - 1].rect;
      const gap = Math.max(list[i].rect[xyDim] - prevItemRect[xyDim] - prevItemRect[sizeDim], 0);
      gaps.push(gap);
      totalGaps += gap;
    }
    if (!totalGaps) {
      return;
    }

    const squeezePercent = Math.min(Math.abs(dlt) / totalGaps, maxSqeezePercent);

    if (dlt > 0) {
      for (let i = 0; i < len - 1; i += 1) {
        // Distribute the shift delta to all gaps.
        const movement = gaps[i] * squeezePercent;
        // Forward
        shiftList(movement, 0, i + 1);
      }
    } else {
      // Backward
      for (let i = len - 1; i > 0; i--) {
        // Distribute the shift delta to all gaps.
        const movement = gaps[i - 1] * squeezePercent;
        shiftList(-movement, i, len);
      }
    }
  }

  /**
   * Squeeze to allow overlap if there is no more space available.
   * Let other overlapping strategy like hideOverlap do the job instead of keep exceeding the bounds.
   */
  function squeezeWhenBailout(dlt: number) {
    const dir = dlt < 0 ? -1 : 1;
    dlt = Math.abs(dlt);
    const moveForEachLabel = Math.ceil(dlt / (len - 1));

    for (let i = 0; i < len - 1; i += 1) {
      if (dir > 0) {
        // Forward
        shiftList(moveForEachLabel, 0, i + 1);
      } else {
        // Backward
        shiftList(-moveForEachLabel, len - i - 1, len);
      }

      dlt -= moveForEachLabel;

      if (dlt <= 0) {
        return;
      }
    }
  }
}
